"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const lodash_1 = __importDefault(require("lodash"));
const _1 = require(".");
const immer_1 = __importDefault(require("immer"));
/** Abstracted schema for a database. Immutable.
 * Stores tables with columns (possibly in nested sections).
 * See types.ts for more info.
 *
 * Also contains any variables and their values.
 *
 * Variables can be either bound or unbound. If they are bound, it means that they currently have a value within the schema.
 * If they are unbound, then the expression cannot be compiled, but can still be validated.
 */
class Schema {
    tables;
    /** Map of table.id to table */
    tableMap;
    /** Map of "<tableid>" to map of { "<columnid>" to column } */
    columnMaps;
    /** Variables in the schema */
    variables;
    /** Values of variables */
    variableValues;
    constructor(schemaJson, variables, variableValues) {
        this.tables = [];
        this.tableMap = {};
        this.columnMaps = {};
        this.variables = variables || [];
        this.variableValues = variableValues || {};
        if (schemaJson) {
            this.tables = schemaJson.tables;
            // Setup maps
            for (let table of this.tables) {
                this.tableMap[table.id] = table;
                this.columnMaps[table.id] = this.indexTable(table);
            }
        }
    }
    /** Add a single variable, replacing any existing one with the same id */
    addVariable(variable, value) {
        if (value === undefined) {
            return this.addVariables([variable], {});
        }
        return this.addVariables([variable], { [variable.id]: value });
    }
    /** Add variables, replacing any existing ones with the same id */
    addVariables(variables, variableValues) {
        const schema = new Schema();
        schema.tables = this.tables;
        schema.tableMap = this.tableMap;
        schema.columnMaps = this.columnMaps;
        schema.variables = (0, immer_1.default)(this.variables, draft => {
            for (let variable of variables) {
                const index = draft.findIndex(v => v.id === variable.id);
                if (index !== -1) {
                    draft[index] = variable;
                }
                else {
                    draft.push(variable);
                }
            }
        });
        schema.variableValues = (0, immer_1.default)(this.variableValues, draft => {
            for (let variableId in variableValues) {
                draft[variableId] = variableValues[variableId];
            }
        });
        return schema;
    }
    indexTable(table) {
        return lodash_1.default.indexBy((0, _1.flattenContents)(table.contents), (c) => c.id);
    }
    getTables() {
        return this.tables;
    }
    getTable(tableId) {
        return this.tableMap[tableId] || null;
    }
    getColumn(tableId, columnId) {
        const map = this.columnMaps[tableId];
        if (!map) {
            return null;
        }
        return map[columnId] || null;
    }
    /** Gets the columns in order, flattened out from sections */
    getColumns(tableId) {
        return (0, _1.flattenContents)(this.getTable(tableId).contents);
    }
    getVariables() {
        return this.variables;
    }
    getVariableValues() {
        return this.variableValues;
    }
    /** Add table with id, name, desc, primaryKey, ordering (column with natural order) and contents (array of columns/sections)
     * Will replace table if already exists.
     * schemas are immutable, so returns a fresh copy */
    addTable(table) {
        // Remove existing and add new
        const tables = lodash_1.default.filter(this.tables, (t) => t.id !== table.id);
        tables.push(table);
        // Update table map
        const tableMap = lodash_1.default.clone(this.tableMap);
        tableMap[table.id] = table;
        // Update column map
        const columnMaps = lodash_1.default.clone(this.columnMaps);
        columnMaps[table.id] = this.indexTable(table);
        const schema = new Schema();
        schema.tables = tables;
        schema.tableMap = tableMap;
        schema.columnMaps = columnMaps;
        return schema;
    }
    // Convert to a JSON
    toJSON() {
        return { tables: this.tables };
    }
}
exports.default = Schema;
